import moment from 'moment/moment'
import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)
import VuexPersistence from 'vuex-persist';
const vuexLocal = new VuexPersistence({
    storage: window.localStorage,
    key: 'seznamka',
    reducer: state => ({
        user: state.user,
        token: state.token,
    })
})

//const URL = "http://localhost:3000/";
const URL = process.env.VUE_APP_API || "https://backend.e-see-you.cz/";

export { URL };

/**
 * Hooks the UI registers (see App.vue) so the store can talk to the user
 * without importing components: show a toast and ask for a new login.
 */
export const apiEvents = {
    notify: null,
    sessionExpired: null,
};

const MESSAGES = {
    network: "Nepodařilo se spojit se serverem. Zkontrolujte připojení k internetu a zkuste to znovu.",
    timeout: "Server neodpověděl včas. Zkuste to prosím znovu.",
    server: "Na serveru došlo k chybě. Zkuste to prosím za chvíli znovu.",
    unavailable: "Server je momentálně nedostupný. Zkuste to prosím za chvíli znovu.",
    rateLimit: "Příliš mnoho požadavků. Chvíli počkejte a zkuste to znovu.",
    tooLarge: "Odesílaná data jsou příliš velká.",
    notFound: "Požadovaná položka nebyla nalezena.",
    forbidden: "K této akci nemáte oprávnění.",
    generic: "Požadavek se nepodařilo dokončit. Zkuste to prosím znovu.",
    sessionExpired: "Platnost přihlášení vypršela, přihlaste se prosím znovu.",
};

function defaultMessage(status) {
    if (status === 401) return MESSAGES.sessionExpired;
    if (status === 403) return MESSAGES.forbidden;
    if (status === 404) return MESSAGES.notFound;
    if (status === 413) return MESSAGES.tooLarge;
    if (status === 429) return MESSAGES.rateLimit;
    if (status === 502 || status === 503 || status === 504) return MESSAGES.unavailable;
    if (status >= 500) return MESSAGES.server;
    return MESSAGES.generic;
}

let lastNotification = { message: null, time: 0 };

/** Shows an error toast; the same text is not repeated within a few seconds. */
export function notifyError(message, duration = 8) {
    const now = Date.now();
    if (lastNotification.message === message && now - lastNotification.time < 4000) return;
    lastNotification = { message, time: now };

    if (apiEvents.notify) apiEvents.notify({ message, duration, type: "error" });
}

const SESSION_CODES = ["token_expired", "token_invalid"];

/**
 * Single entry point for every API call. Never throws: it always resolves to
 *   { ok, status, data, message, code, network }
 * and, unless `silent`, has already told the user what went wrong.
 *
 * options:
 *   method, json (object body), formData, auth (send the token, default true),
 *   silent (no toast for network/server failures – for background polling),
 *   timeout (ms), retryAnonymous (repeat without a token once the session turns out to be dead)
 */
async function request({ state, commit }, path, options = {}) {
    const {
        method = "GET",
        json,
        formData,
        auth = true,
        silent = false,
        timeout = 30000,
        retryAnonymous = false,
    } = options;

    const token = auth ? state.token : undefined;

    const headers = {};
    if (json !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers["x-auth-token"] = token;

    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeout) : null;

    let response;
    try {
        response = await fetch(URL + path, {
            method,
            headers,
            body: formData || (json !== undefined ? JSON.stringify(json) : undefined),
            signal: controller ? controller.signal : undefined,
            cors: "cors",
        });
    } catch (e) {
        const timedOut = !!e && e.name === "AbortError";
        const message = timedOut ? MESSAGES.timeout : MESSAGES.network;
        if (!silent) notifyError(message);
        return { ok: false, status: 0, message, network: true };
    } finally {
        if (timer) clearTimeout(timer);
    }

    let body = null;
    try {
        body = await response.json();
    } catch (e) {
        body = null;
    }

    if (response.ok && !(body && body.status === "error")) {
        return { ok: true, status: response.status, data: body ? body.data : undefined, body };
    }

    const code = body && body.code;
    const message = (body && typeof body.message === "string" && body.message) || defaultMessage(response.status);

    // The stored login is dead (expired token, deleted or deactivated account): drop it and ask for a new login.
    if (response.status === 401 && token && (!code || SESSION_CODES.includes(code))) {
        if (state.token === token) {
            commit("setToken", undefined);
            commit("setUser", undefined);
            notifyError(message);
            if (apiEvents.sessionExpired) apiEvents.sessionExpired();
        }

        if (retryAnonymous) {
            return request({ state, commit }, path, { ...options, auth: false });
        }

        return { ok: false, status: 401, message, code, sessionExpired: true };
    }

    // Background polling stays quiet; only a dead session (handled above) is always reported.
    if (!silent) notifyError(message);

    return { ok: false, status: response.status, message, code };
}

export default new Vuex.Store({
    plugins: [vuexLocal.plugin],
    state: {
        token: undefined,
        user: undefined,

        chat: undefined,
        chats: [],

        searches: [],

        stats: {
            totalUsers: 0,
            totalSearches: 0,
            activeUsers: 0,
        }
    },
    mutations: {
        setToken(state, data) {
            // Another login must not inherit the previous person's open chat (the socket would refuse it as forbidden).
            if (state.token !== data) {
                state.chat = undefined;
                state.chats = [];
            }
            state.token = data
        },
        setUser(state, data) {
            state.user = data
        },

        setChat(state, data) {
            state.chat = data
        },

        setChats(state, data) {
            state.chats = data
        },

        setSearches(state, data) {
            state.searches = data
        },

        setStats(state, data) {
            state.stats = data
        }
    },
    actions: {
        getSource({ state}, data) {
            return URL;
        },

        setToken({ commit }, data) {
            commit('setToken', data)
        },

        async loadUser(ctx) {
            if (!ctx.state.token) return false;

            const tokenAtStart = ctx.state.token;
            const result = await request(ctx, "user/@me", { silent: true });

            if (!result.ok || !result.data || !result.data.user) return false;

            // The user logged out (or logged in as someone else) while this was in flight – drop the stale answer,
            // otherwise it would silently log them back in.
            if (ctx.state.token !== tokenAtStart) return false;

            // Sliding session: the server hands out a fresh token before the old one expires.
            if (result.data.token) ctx.commit('setToken', result.data.token);

            const user = result.data.user;
            ctx.commit('setUser', {
                ...user,

                lastAuth: moment(user.lastAuth),
                createdAt: moment(user.createdAt),
                birthDate: moment(user.birthDate),
            });

            return true;
        },

        /** Resolves to the list of searches, or null when loading failed (the user has been told why). */
        async loadSearch(ctx, params) {
            const result = await request(ctx, "search/list?" + new URLSearchParams(params), { retryAnonymous: true });

            if (!result.ok) return null;

            return (result.data && result.data.searches) || [];
        },

        /** Resolves to true when the search was saved. On failure the user has been told why. */
        async putSearch(ctx, params) {
            const result = await request(ctx, "search", { method: "POST", json: params });

            return result.ok;
        },

        async createChat(ctx, params) {
            const result = await request(ctx, "chat", {
                method: "POST",
                json: {
                    message: params.message,
                    search: params.search
                },
            });

            if (!result.ok || !result.data) return null;

            ctx.commit('setChat', {
                id: result.data.chat
            });

            return result.data;
        },

        async getChats(ctx, options = {}) {
            const result = await request(ctx, "chat/list", { silent: !!options.silent });

            if (!result.ok || !result.data) return false;

            ctx.commit('setChats', result.data.chats || []);

            return true;
        },

        async updateUser(ctx, params) {
            const result = await request(ctx, "user/@me", { method: "PUT", json: params });

            if (!result.ok) return false;

            ctx.dispatch("loadUser");

            return true;
        },

        async updateUserPassword(ctx, data) {
            const result = await request(ctx, "user/@me/password", { method: "PATCH", json: data });

            return result.ok;
        },

        async requestDeletion(ctx, data) {
            const result = await request(ctx, "user/@me", { method: "DELETE", json: data });

            return result.ok;
        },
        async deleteUser(ctx, data) {
            const result = await request(ctx, "user/@me/" + data, { method: "DELETE" });

            return result.ok;
        },

        async uploadProfilePicture(ctx, params) {
            const formData = new FormData();
            formData.append("profilePicture", params);

            const result = await request(ctx, "user/@me/picture", { method: "POST", formData, timeout: 120000 });

            if (!result.ok) return false;

            ctx.dispatch("loadUser");

            return true;
        },
        async uploadPicture(ctx, params) {
            const formData = new FormData();
            formData.append("picture", params);

            const result = await request(ctx, "user/@me/pictures", { method: "POST", formData, timeout: 120000 });

            if (!result.ok) return false;

            ctx.dispatch("loadUser");

            return true;
        },
        async deletePicture(ctx, params) {
            const result = await request(ctx, "user/@me/pictures/" + params, { method: "DELETE" });

            if (!result.ok) return false;

            ctx.dispatch("loadUser");

            return true;
        },

        /** Resolves to { ok, message, status, network }; the dialog shows `message` itself (no toast). */
        async logIn(ctx, credentials) {
            const result = await request(ctx, "auth/in", { method: "POST", json: credentials, auth: false, silent: true });

            if (!result.ok || !result.data || !result.data.token) {
                return {
                    ok: false,
                    status: result.status,
                    code: result.code,
                    network: !!result.network,
                    message: result.message || MESSAGES.generic,
                };
            }

            ctx.commit('setToken', result.data.token);

            return { ok: true };
        },

        /**
         * Sends a temporary password to the e-mail address; it works for logging in next to the old password.
         * Always resolves to { ok, message } – on failure the message says why.
         */
        async resetPassword(ctx, { email }) {
            const result = await request(ctx, "auth/reset", { method: "POST", json: { email }, auth: false, silent: true, timeout: 60000 });

            if (result.ok) {
                return { ok: true, message: (result.data && result.data.message) || "" };
            }

            return { ok: false, message: result.message || MESSAGES.generic };
        },

        logOut({ commit }) {
            commit('setToken', undefined);
            commit('setUser', undefined);
        },


        /**
         * Always resolves to { status: "success" | "error", message? } so the form can show the reason.
         */
        async register(ctx, params) {
            const formData = new FormData();

            formData.append("photo", params.photo);
            formData.append("data", JSON.stringify(params.data))

            const result = await request(ctx, "user", { method: "POST", formData, auth: false, timeout: 120000 });

            // emailSent is false when the account exists but the activation e-mail could not be delivered.
            if (result.ok) return { status: "success", emailSent: !(result.data && result.data.emailSent === false) };

            return { status: "error", message: result.message };
        },

        /**
         * Activates the account with the 8-digit code from the e-mail (or the token of an older e-mail link).
         * Always resolves to { ok, message } – on failure the message says why.
         */
        async activate(ctx, code) {
            const result = await request(ctx, "user/activate/" + encodeURIComponent(code), { method: "POST", auth: false, silent: true });

            return { ok: result.ok, message: result.ok ? "" : (result.message || MESSAGES.generic) };
        },

        async resendActivation(ctx, email) {
            const result = await request(ctx, "user/activation/resend", { method: "POST", json: { email }, auth: false, silent: true, timeout: 60000 });

            return { ok: result.ok, message: result.ok ? "" : (result.message || MESSAGES.generic) };
        },

        async getMySearches(ctx) {
            const result = await request(ctx, "search/@me");

            if (!result.ok || !result.data) return false;

            ctx.commit('setSearches', result.data.searches || []);

            return true;
        },

        async patchSearch(ctx, data) {
            const result = await request(ctx, "search/" + data.id, {
                method: "PATCH",
                json: { active: data.active },
            });

            if (!result.ok) return false;

            await ctx.dispatch("getMySearches");

            return true;
        },
        async deleteSearch(ctx, data) {
            const result = await request(ctx, "search/" + data, { method: "DELETE" });

            if (!result.ok) return false;

            await ctx.dispatch("getMySearches");

            return true;
        },

        async getStats(ctx) {
            const result = await request(ctx, "stats", { auth: false, silent: true });

            if (!result.ok || !result.data) return false;

            ctx.commit('setStats', result.data);

            return true;
        },

        async getUserStats(ctx) {
            const result = await request(ctx, "stats/users", { auth: false, silent: true });

            return result.ok ? result.data : null;
        },

        async reportUser(ctx, data) {
            const result = await request(ctx, `user/${data.reported}/report`, {
                method: "POST",
                json: { details: data.details },
            });

            return result.ok;
        },

        async registerPushToken({ state }, token) {
            try {
                if (!state.token) return false;
                const masked = (typeof token === 'string' && token.length > 12) ? `${token.slice(0,6)}…${token.slice(-6)}` : String(token);
                console.log('[PUSH][client] registering APNs token', { url: URL + 'push/register', token: masked });
                const response = await fetch(URL + "push/register", {
                    method: "POST",
                    cors: "cors",
                    headers: {
                        "Content-Type": "application/json",
                        "x-auth-token": state.token,
                    },
                    body: JSON.stringify({ token, platform: 'ios' }),
                });
                console.log('[PUSH][client] register response', { ok: response.ok, status: response.status });
                return response.ok;
            } catch (e) {
                console.error('registerPushToken failed', e);
                return false;
            }
        },

        async unregisterPushToken({ state }, token) {
            try {
                if (!state.token) return false;
                const response = await fetch(URL + "push/register", {
                    method: "DELETE",
                    cors: "cors",
                    headers: {
                        "Content-Type": "application/json",
                        "x-auth-token": state.token,
                    },
                    body: JSON.stringify({ token }),
                });
                return response.ok;
            } catch (e) {
                console.error('unregisterPushToken failed', e);
                return false;
            }
        }
    },
    getters: {
        facebookLoginUrl: () => {
            return URL + "auth/facebook";
        },
    },
    modules: {
    }
})
